
import React, { useEffect, useState } from 'react';
import { Heart } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { contentService } from '../../services/contentService';

interface LikeButtonProps {
  contentPageId: string;
  initialLikeCount?: number;
  className?: string;
  source?: 'content' | 'article';
}

const LikeButton: React.FC<LikeButtonProps> = ({
  contentPageId,
  initialLikeCount = 0,
  className = '',
  source = 'content',
}) => {
  const { state } = useAuth();
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [isLiked, setIsLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setLikeCount(initialLikeCount);
    setIsLiked(false);

    if (!state.isAuthenticated) return;

    let active = true;
    contentService.getLikeStatus(contentPageId, source)
      .then(({ liked, likeCount: storedLikeCount }) => {
        if (active) {
          setIsLiked(liked);
          if (typeof storedLikeCount === 'number') setLikeCount(storedLikeCount);
        }
      })
      .catch((error) => console.error('Failed to load like status:', error));

    return () => { active = false; };
  }, [contentPageId, initialLikeCount, source, state.isAuthenticated, state.user?.id]);

  const handleLike = async () => {
    if (!state.isAuthenticated) {
      // TODO: Show login prompt or redirect
      return;
    }

    if (isLoading) return;

    try {
      setIsLoading(true);

      const result = isLiked
        ? await contentService.unlike(contentPageId, source)
        : await contentService.like(contentPageId, source);
      setLikeCount(result.likeCount);
      setIsLiked(result.liked);
    } catch (error) {
      console.error('Failed to like content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleLike}
      disabled={!state.isAuthenticated || isLoading}
      className={`
        article-like-button ${isLiked ? 'is-liked' : 'is-unliked'} flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200
        ${isLiked
          ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-600 dark:text-red-400'
          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-red-400 hover:text-red-600 dark:hover:text-red-400'
        }
        ${!state.isAuthenticated ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      aria-label={isLiked ? 'Unlike this article' : 'Like this article'}
    >
      <Heart
        className={`w-5 h-5 transition-all duration-200 ${isLiked ? 'fill-red-600 dark:fill-red-400' : ''
          }`}
      />
      <span className="font-medium">{likeCount}</span>
      {isLoading && (
        <span className="ml-2 text-xs text-gray-500">...</span>
      )}
    </button>
  );
};

export default LikeButton;
